import { useState, useEffect, useCallback, useRef } from "react";
import { MapPin, Loader2, X } from "lucide-react";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";

export interface AddressResult {
  displayName: string;
  address: string;
  city: string;
  province: string;
  latitude: number;
  longitude: number;
}

interface AddressAutocompleteProps {
  value: string;
  onValueChange: (value: string, address?: AddressResult) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function AddressAutocomplete({
  value,
  onValueChange,
  placeholder = "Cerca indirizzo...",
  className,
  disabled = false,
}: AddressAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(value || "");
  const [results, setResults] = useState<AddressResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<AddressResult | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(searchQuery, 300);

  // Search addresses via Nominatim API
  const searchAddresses = useCallback(async (query: string) => {
    if (!query || query.length < 3) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      // Use Nominatim API directly (free, no auth needed)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=it&limit=8&addressdetails=1`,
        {
          headers: {
            'Accept-Language': 'it',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();

      const addresses: AddressResult[] = data.map((item: any) => {
        const addr = item.address || {};
        const road = addr.road || addr.pedestrian || addr.footway || '';
        const houseNumber = addr.house_number || '';
        const city = addr.city || addr.town || addr.village || addr.municipality || '';
        const province = addr.county || addr.state || '';

        // Create full address
        let fullAddress = '';
        if (road) {
          fullAddress = houseNumber ? `${road}, ${houseNumber}` : road;
        } else if (item.display_name) {
          fullAddress = item.display_name.split(',')[0];
        }

        // Create display name
        const displayParts = [fullAddress];
        if (city && !fullAddress.includes(city)) {
          displayParts.push(city);
        }
        if (province && province !== city && !fullAddress.includes(province)) {
          displayParts.push(province);
        }
        const displayName = displayParts.filter(Boolean).join(', ');

        return {
          displayName,
          address: fullAddress,
          city,
          province,
          latitude: parseFloat(item.lat),
          longitude: parseFloat(item.lon),
        };
      });

      // Remove duplicates based on displayName
      const uniqueAddresses = addresses.filter(
        (addr, index, self) =>
          index === self.findIndex((a) => a.displayName === addr.displayName)
      );

      setResults(uniqueAddresses);
    } catch (error) {
      console.error('Error searching addresses:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Effect to search when debounced query changes
  useEffect(() => {
    if (debouncedQuery && !selectedAddress) {
      searchAddresses(debouncedQuery);
    } else if (!debouncedQuery) {
      setResults([]);
    }
  }, [debouncedQuery, searchAddresses, selectedAddress]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (address: AddressResult) => {
    setSelectedAddress(address);
    setSearchQuery(address.displayName);
    onValueChange(address.displayName, address);
    setIsOpen(false);
    setResults([]);
  };

  const handleClear = () => {
    setSelectedAddress(null);
    setSearchQuery('');
    onValueChange('', undefined);
    setResults([]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    setSelectedAddress(null);
    onValueChange(newValue, undefined);
    if (newValue.length >= 3) {
      setIsOpen(true);
    }
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <Input
          icon={<MapPin className="h-4 w-4" />}
          placeholder={placeholder}
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => {
            if (searchQuery.length >= 3 && !selectedAddress) {
              setIsOpen(true);
            }
          }}
          disabled={disabled}
          className={cn(
            selectedAddress && "border-emerald-500/50 bg-emerald-500/5"
          )}
        />
        {(searchQuery || selectedAddress) && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-card shadow-lg overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Ricerca...</span>
            </div>
          ) : results.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">
              {searchQuery.length < 3
                ? "Digita almeno 3 caratteri..."
                : "Nessun indirizzo trovato"}
            </div>
          ) : (
            <div className="max-h-60 overflow-auto">
              {results.map((address, index) => (
                <button
                  key={`${address.displayName}-${index}`}
                  type="button"
                  className="w-full px-3 py-2.5 text-left hover:bg-secondary/50 transition-colors border-b border-border last:border-b-0"
                  onClick={() => handleSelect(address)}
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#284899]" />
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{address.displayName}</p>
                      {address.province && address.province !== address.city && (
                        <p className="text-sm text-muted-foreground">{address.province}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
